import { computed, getCurrentInstance, ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { findSpotTarget } from 'dashboard/composables/useFocusSpotlight';
import { routeViewOf } from 'dashboard/helper/onboardingRegistry';
import {
  TOUR_STATUS,
  TARGET_WAIT_ATTEMPTS,
  TARGET_WAIT_INTERVAL_MS,
  clampStepIndex,
  shouldResumeTour,
  shouldStartTour,
  tourForRoute,
  tourRecordOf,
} from 'dashboard/helper/onboardingTours';

// Bloqueia o card de dica enquanto o convite ou o tour desta tela está aberto.
export const tourBlocksCoach = ref(false);

// Sobe a cada tour concluido. O botao do Modo Foco escuta para comemorar.
export const tourFinishedToken = ref(0);

// Recusa desta visita. Quem segura de verdade e o registro PULADO em
// ui_settings; o Set so cobre a janela ate o ui_settings voltar do servidor.
const declinedThisVisit = new Set();
// Tour pausado pela propria pessoa (Esc) nesta visita da rota. Sem isto o
// watcher de toursMap via o registro 'pausado' chegar e retomava o tour no
// mesmo tick, entao Esc nunca pausava de verdade. Limpa ao sair da rota.
const pausedThisVisit = new Set();
const replayToken = ref(0);
const replayKey = ref(null);

export function requestTourReplay(key) {
  if (!key) return;
  declinedThisVisit.delete(key);
  replayKey.value = key;
  replayToken.value += 1;
}

export function resetGuidedTourSession() {
  declinedThisVisit.clear();
  pausedThisVisit.clear();
  tourBlocksCoach.value = false;
  replayKey.value = null;
}

const waitForTourTarget = (selectors = [], attempts = TARGET_WAIT_ATTEMPTS) => {
  const list = (selectors || []).filter(Boolean);
  return new Promise(resolve => {
    if (!list.length) {
      resolve(null);
      return;
    }
    const tick = left => {
      const hit = list.find(selector => findSpotTarget(selector));
      if (hit || left <= 0) {
        resolve(hit || null);
        return;
      }
      if (typeof window === 'undefined') {
        resolve(null);
        return;
      }
      window.setTimeout(() => tick(left - 1), TARGET_WAIT_INTERVAL_MS);
    };
    tick(attempts);
  });
};

const emptyTours = () => ({});

export function useGuidedTour({ host = true } = {}) {
  const route = useRoute();
  const { uiSettings, updateUISettings } = useUISettings();

  const toursMap = computed(() => {
    const raw = uiSettings.value?.guided_tours;
    return raw && typeof raw === 'object' && !Array.isArray(raw)
      ? raw
      : emptyTours();
  });

  const { enabled: surfaceEnabled } = useOnboardingSurface();
  const currentTour = computed(() =>
    tourForRoute(route.name, routeViewOf(route))
  );
  const currentRecord = computed(() =>
    tourRecordOf(toursMap.value, currentTour.value?.key)
  );

  const inviteOpen = ref(false);
  const running = ref(false);
  const floating = ref(false);
  const activeKey = ref(null);
  const activeSelector = ref('');
  const stepIndex = ref(0);
  const semAncora = ref({});
  const nudged = ref(false);
  let waitToken = 0;

  const currentStep = computed(() => {
    const tour = currentTour.value;
    if (!tour) return null;
    return tour.steps[stepIndex.value] || null;
  });

  const stepCount = computed(() => currentTour.value?.steps?.length || 0);

  const patchTours = next => {
    updateUISettings({ guided_tours: { ...next } });
  };

  const writeRecord = (key, partial) => {
    if (!key) return;
    const previous = tourRecordOf(toursMap.value, key) || {};
    patchTours({
      ...toursMap.value,
      [key]: { ...previous, ...partial },
    });
  };

  const resetUi = () => {
    running.value = false;
    inviteOpen.value = false;
    floating.value = false;
    activeKey.value = null;
    activeSelector.value = '';
    nudged.value = false;
  };

  const closeUi = () => {
    resetUi();
    tourBlocksCoach.value = false;
  };

  const setBlocking = value => {
    tourBlocksCoach.value = value;
  };

  const resolveStepTarget = async (tour, index, token) => {
    const step = tour.steps[index];
    if (!step) return { selector: '', missing: true };
    const hit = await waitForTourTarget(step.anchors);
    if (token !== waitToken) return { cancelled: true };
    if (hit) return { selector: hit, missing: false };
    return { selector: '', missing: true };
  };

  const openStep = async (tour, index) => {
    if (!tour) return;
    waitToken += 1;
    const token = waitToken;
    const clamped = clampStepIndex(tour, index);
    stepIndex.value = clamped;
    activeKey.value = tour.key;
    inviteOpen.value = false;
    running.value = true;
    setBlocking(true);
    const step = tour.steps[clamped];
    writeRecord(tour.key, {
      version: tour.version,
      step: clamped,
      status: TOUR_STATUS.PENDENTE,
      started: true,
    });
    const result = await resolveStepTarget(tour, clamped, token);
    if (result.cancelled) return;
    if (result.missing) {
      floating.value = true;
      activeSelector.value = '';
      semAncora.value = { ...semAncora.value, [step.id]: true };
      writeRecord(tour.key, {
        semAncora: {
          ...(currentRecord.value?.semAncora || {}),
          [step.id]: true,
        },
      });
      return;
    }
    floating.value = false;
    activeSelector.value = result.selector;
  };

  const showInvite = tour => {
    if (!tour) return;
    waitToken += 1;
    activeKey.value = tour.key;
    inviteOpen.value = true;
    running.value = false;
    floating.value = true;
    activeSelector.value = '';
    setBlocking(true);
    // Convite mostrado ja vale como respondido: gravar 'pendente' aqui fazia o
    // convite ignorado (navegou embora, deploy recarregou a pagina) voltar em
    // TODA visita da tela, pra sempre. Quem quiser rever tem o menu do Brasa e
    // a Central de ajuda, e o bump de version reconvida todo mundo.
    writeRecord(tour.key, {
      version: tour.version,
      step: currentRecord.value?.step || 0,
      status: TOUR_STATUS.PULADO,
      started: false,
    });
  };

  const evaluateRoute = () => {
    waitToken += 1;
    resetUi();
    const tour = currentTour.value;
    if (!surfaceEnabled.value || !tour || declinedThisVisit.has(tour.key)) {
      tourBlocksCoach.value = false;
      return;
    }
    const record = tourRecordOf(toursMap.value, tour.key);
    if (!shouldStartTour(tour, record)) {
      tourBlocksCoach.value = false;
      return;
    }
    if (shouldResumeTour(tour, record)) {
      openStep(tour, record.step || 0);
      return;
    }
    showInvite(tour);
  };

  const start = (key = currentTour.value?.key) => {
    const tour = currentTour.value;
    if (!tour || (key && tour.key !== key)) return;
    declinedThisVisit.delete(tour.key);
    pausedThisVisit.delete(tour.key);
    semAncora.value = {};
    openStep(tour, 0);
  };

  const restart = (key = currentTour.value?.key) => {
    const tour = currentTour.value;
    if (!tour || (key && tour.key !== key)) return;
    declinedThisVisit.delete(tour.key);
    pausedThisVisit.delete(tour.key);
    semAncora.value = {};
    writeRecord(tour.key, {
      version: tour.version,
      step: 0,
      status: TOUR_STATUS.PENDENTE,
      started: true,
      semAncora: {},
    });
    openStep(tour, 0);
  };

  const finish = () => {
    const tour = currentTour.value;
    if (!tour) return;
    writeRecord(tour.key, {
      version: tour.version,
      step: Math.max(0, tour.steps.length - 1),
      status: TOUR_STATUS.CONCLUIDO,
      started: true,
    });
    tourFinishedToken.value += 1;
    waitToken += 1;
    closeUi();
  };

  const next = () => {
    const tour = currentTour.value;
    if (!tour || !running.value) return;
    const upcoming = stepIndex.value + 1;
    if (upcoming >= tour.steps.length) {
      finish();
      return;
    }
    openStep(tour, upcoming);
  };

  const prev = () => {
    const tour = currentTour.value;
    if (!tour || !running.value) return;
    if (stepIndex.value <= 0) return;
    openStep(tour, stepIndex.value - 1);
  };

  // "Agora nao" vale para sempre nesta versao do tour. Grava PULADO, o status
  // que shouldStartTour ja entende como "nao convide mais"; started false
  // separa "recusei o convite" de "pulei no meio do tour". Antes gravava
  // PENDENTE e so o Set em memoria segurava, entao o convite voltava no F5.
  // Quem quiser rever tem o menu do Brasa e a Central de ajuda, e o bump de
  // version reconvida todo mundo.
  const declineInvite = () => {
    const tour = currentTour.value;
    if (!tour) return;
    declinedThisVisit.add(tour.key);
    writeRecord(tour.key, {
      version: tour.version,
      step: 0,
      status: TOUR_STATUS.PULADO,
      started: false,
    });
    waitToken += 1;
    closeUi();
  };

  const pause = () => {
    const tour = currentTour.value;
    if (!tour || (!running.value && !inviteOpen.value)) return;
    // Esc no convite nao pausa nada, porque nao ha passo salvo: e recusa, e
    // segue o mesmo caminho do "Agora nao" para nao convidar de novo depois.
    if (!running.value) {
      declineInvite();
      return;
    }
    pausedThisVisit.add(tour.key);
    writeRecord(tour.key, {
      version: tour.version,
      step: stepIndex.value,
      status: TOUR_STATUS.PAUSADO,
      started: true,
    });
    waitToken += 1;
    closeUi();
  };

  const skip = () => {
    const tour = currentTour.value;
    if (!tour) return;
    writeRecord(tour.key, {
      version: tour.version,
      step: stepIndex.value,
      status: TOUR_STATUS.PULADO,
      started: true,
    });
    waitToken += 1;
    closeUi();
  };

  const markNudged = () => {
    nudged.value = true;
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      nudged.value = false;
    }, 420);
  };

  const advanceFromEvent = event => {
    const step = currentStep.value;
    if (!running.value || !step?.advanceOn) return false;
    if (event.type !== step.advanceOn.event) return false;
    const selector = step.advanceOn.selector;
    const node = event.target;
    if (!node || typeof node.closest !== 'function') return false;
    if (!node.closest(selector)) return false;
    markNudged();
    next();
    return true;
  };

  if (host) {
    watch(
      () => [route.name, routeViewOf(route)],
      (_current, previous) => {
        const [previousName, previousView] = previous || [];
        if (previousName) {
          const leaving = tourForRoute(previousName, previousView);
          if (leaving && leaving.key !== currentTour.value?.key) {
            declinedThisVisit.delete(leaving.key);
            pausedThisVisit.delete(leaving.key);
          }
          if (leaving && running.value) {
            writeRecord(leaving.key, {
              version: leaving.version,
              step: stepIndex.value,
              status: TOUR_STATUS.PAUSADO,
              started: true,
            });
          }
        }
        evaluateRoute();
      },
      { immediate: true }
    );

    watch(surfaceEnabled, enabled => {
      if (!enabled) {
        waitToken += 1;
        closeUi();
        return;
      }
      evaluateRoute();
    });

    watch(replayToken, () => {
      const key = replayKey.value;
      if (!key) return;
      if (currentTour.value?.key === key) restart(key);
    });

    watch(
      () => [toursMap.value, currentTour.value?.key],
      () => {
        if (running.value || inviteOpen.value) return;
        if (!currentTour.value) return;
        if (declinedThisVisit.has(currentTour.value.key)) return;
        if (pausedThisVisit.has(currentTour.value.key)) return;
        if (shouldStartTour(currentTour.value, currentRecord.value)) {
          evaluateRoute();
        }
      }
    );

    if (getCurrentInstance()) {
      onBeforeUnmount(() => {
        waitToken += 1;
        closeUi();
      });
    }
  }

  return {
    currentTour,
    currentRecord,
    currentStep,
    stepIndex,
    stepCount,
    inviteOpen,
    running,
    floating,
    activeKey,
    activeSelector,
    semAncora,
    nudged,
    start,
    restart,
    next,
    prev,
    pause,
    skip,
    finish,
    declineInvite,
    evaluateRoute,
    advanceFromEvent,
    closeUi,
  };
}
